import { useContext } from 'react';
import {
  destroyToken,
  destroyUserData,
  setAuthToken,
  TOKEN_USER_DATA,
} from '../utils/authentication';
import agent from '../agent';
import { navigate } from 'gatsby';
import { toDashboard } from '../routes';
import analytics, {
  ENGAGEMENT_CATEGORY,
  LOGIN_ACTION,
  LOGOUT_ACTION,
} from '../analytics';
import { GlobalContext } from '../context/GlobalContext';

export type UserTokenType = { sub: string; role: string; delegated: boolean };

export type UserAuthType = {
  username?: string;
  isAuthenticated: boolean;
  isEmployer: boolean;
  isStaff: boolean;
  isDelegated: boolean;
};

type DelegatedAuthType = {
  username: string | string[] | null;
  password: string | string[] | null;
  delegated: boolean;
  setErrorMessage: (message: string) => void;
};

type PrimaryAuthType = {
  formElements: {
    email: { value: string };
    password: { value: string };
  };
  setErrorMessage: (message: string) => void;
};

function isDelegatedAuth(obj: any): obj is DelegatedAuthType {
  return !!obj && 'username' in obj;
}

export const INITIAL_USER_AUTH: UserAuthType = {
  username: undefined,
  isAuthenticated: false,
  isEmployer: false,
  isStaff: false,
  isDelegated: false,
};

export function getUserAuth() {
  if (typeof window !== 'undefined') {
    const token = window.localStorage.getItem(TOKEN_USER_DATA);
    if (!!token) {
      const userData: UserTokenType = JSON.parse(token);
      return {
        username: userData.sub,
        isAuthenticated: !!userData,
        isEmployer: userData.role === 'employer',
        isStaff: userData.role === 'staff',
        isDelegated: userData.delegated,
      };
    } else {
      return INITIAL_USER_AUTH;
    }
  } else {
    return INITIAL_USER_AUTH;
  }
}

export default function useLocalStorageAuth() {
  const { userAuth, setAuthError } = useContext(GlobalContext);

  function login(props: PrimaryAuthType | DelegatedAuthType) {
    return agent.Auth.login({
      username: isDelegatedAuth(props)
        ? props.username
        : props.formElements.email.value.trim(),
      password: isDelegatedAuth(props)
        ? props.password
        : props.formElements.password.value,
      delegated: isDelegatedAuth(props),
    })
      .then((res) => {
        if (!isDelegatedAuth(props)) {
          analytics.Events.trackEvent({
            actionType: LOGIN_ACTION,
            category: ENGAGEMENT_CATEGORY,
            payload: {
              role: userAuth.isEmployer
                ? 'employer'
                : userAuth.isStaff
                  ? 'staff'
                  : undefined,
              distinctId: userAuth.username,
              email: props.formElements.email.value.trim(),
            },
          });
        }
        if (res.status === 200 && res.headers.authorization) {
          setAuthToken(res.headers.authorization);
          window.dispatchEvent(new Event('authSuccess'));
        }
        navigate(toDashboard);
      })
      .catch((err) => {
        props.setErrorMessage(err.message);
      });
  }

  function resetStorage(callback?: () => void) {
    destroyToken();
    destroyUserData();
    window.dispatchEvent(new Event('authFailure'));
    if (!!callback) {
      callback();
    }
  }

  function logout(isDelegated?: boolean, callback?: () => void) {
    return agent.Auth.logout()
      .then((res) => {
        if (!isDelegated) {
          analytics.Events.trackEvent({
            actionType: LOGOUT_ACTION,
            category: ENGAGEMENT_CATEGORY,
          });
        }
        resetStorage(callback);
      })
      .catch((err) => {
        setAuthError(err.message);
        resetStorage(callback);
      });
  }

  return {
    login,
    logout,
  };
}
